/* src/render/interiorArchitecture.ts — the building's inside, planned and built as pure data.
 *
 * Everything about the room that is arithmetic lives here: which wall faces the street, where
 * the door and windows are cut, where fixtures may stand, where the stairwell goes, and the
 * triangles for all of it. No Three.js — the module emits plain typed-array bundles that
 * render/interiorView.ts wraps into BufferGeometry, which keeps every rule in here testable
 * without a GPU (the same split tileMesh/geometryWorker already use).
 *
 * The architecture is deliberate, not decorative: walls have thickness, openings are real holes
 * (the city outside is visible through them), the stairwell is a real hole in the slab above,
 * and nothing is placed that the player did not buy. An empty building reads as exactly that —
 * a bare, well-lit space with a front door — because empty is now a state the game means.
 */

import earcut from 'earcut';
import type { InteriorFixture } from '../core/events.ts';
import { boundsOf, clearance, pointInPoly, type Bounds, type Poly } from './interiorGeometry.ts';

export const FLOOR_H = 3.3;
export const WALL_T = 0.22;
export const STAIR_LEN = 3.6;
export const STAIR_W = 1.15;

// ── colours ─────────────────────────────────────────────────────────────────────────────

const C = {
  wallPaint: 0xcfc8b8,
  wallBand: 0xbdb5a2,
  ceiling: 0xf2efe8,
  cornice: 0xe4dfd2,
  skirting: 0x5d4a38,
  frame: 0x4a4038,
  sill: 0xd8d2c4,
  doorLeaf: 0x6b4a2f,
  doorHandle: 0xc9b037,
  stairTread: 0x8a6f4d,
  stairRiser: 0x7a6143,
  stairRail: 0x3d342c,
  stairPost: 0x4a4038,
  column: 0xd8d2c4,
  pendantCord: 0x2a2622,
  pendantShade: 0x3d342c,
  bulb: 0xffe9b8,
  shelfWood: 0x8a6f4d,
  shelfBack: 0x6e5639,
  counter: 0x5d4a38,
  counterTop: 0xd8d2c4,
  register: 0x33383f,
  registerScreen: 0x9fd8c9,
  rackSteel: 0xc9622f,
  rackBeam: 0xb35426,
  pallet: 0x9a7b52,
  glassTint: 0xbcd6e8,
} as const;

function rgb(hex: number): [number, number, number] {
  return [((hex >> 16) & 255) / 255, ((hex >> 8) & 255) / 255, (hex & 255) / 255];
}

/** Stable colour for a commodity's little boxes on a shelf, from its id. */
export function itemColour(itemId: string): number {
  let h = 2166136261;
  for (let i = 0; i < itemId.length; i += 1) {
    h ^= itemId.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const hue = (h >>> 0) % 360;
  // HSL → RGB at fixed s/l so every item lands in the same warm-goods family.
  const s = 0.55;
  const l = 0.55;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = l - c / 2;
  const [r, g, b] =
    hue < 60 ? [c, x, 0] : hue < 120 ? [x, c, 0] : hue < 180 ? [0, c, x]
    : hue < 240 ? [0, x, c] : hue < 300 ? [x, 0, c] : [c, 0, x];
  return (
    (Math.round((r + m) * 255) << 16) | (Math.round((g + m) * 255) << 8) | Math.round((b + m) * 255)
  );
}

// ── the mesh accumulator ────────────────────────────────────────────────────────────────

export interface GeometryData {
  readonly positions: Float32Array;
  readonly normals: Float32Array;
  readonly colors: Float32Array;
  readonly uvs: Float32Array | null;
}

class MeshBuilder {
  readonly pos: number[] = [];
  readonly nrm: number[] = [];
  readonly col: number[] = [];
  readonly uv: number[] | null;

  constructor(withUv = false) {
    this.uv = withUv ? [] : null;
  }

  tri(
    ax: number, ay: number, az: number,
    bx: number, by: number, bz: number,
    cx: number, cy: number, cz: number,
    nx: number, ny: number, nz: number,
    c: readonly [number, number, number],
  ): void {
    this.pos.push(ax, ay, az, bx, by, bz, cx, cy, cz);
    this.nrm.push(nx, ny, nz, nx, ny, nz, nx, ny, nz);
    this.col.push(c[0], c[1], c[2], c[0], c[1], c[2], c[0], c[1], c[2]);
    if (this.uv) this.uv.push(ax * 0.5, az * 0.5, bx * 0.5, bz * 0.5, cx * 0.5, cz * 0.5);
  }

  quad(
    a: readonly [number, number, number],
    b: readonly [number, number, number],
    c: readonly [number, number, number],
    d: readonly [number, number, number],
    n: readonly [number, number, number],
    col: readonly [number, number, number],
  ): void {
    this.tri(a[0], a[1], a[2], b[0], b[1], b[2], c[0], c[1], c[2], n[0], n[1], n[2], col);
    this.tri(a[0], a[1], a[2], c[0], c[1], c[2], d[0], d[1], d[2], n[0], n[1], n[2], col);
  }

  /** Axis-aligned box centred at (cx, cy, cz). */
  box(cx: number, cy: number, cz: number, sx: number, sy: number, sz: number, hex: number): void {
    this.orientedBox(cx, cy, cz, sx, sy, sz, 1, 0, hex);
  }

  /**
   * A box whose length axis lies along the ground direction (dx, dz) (unit), height along y,
   * thickness along the ground perpendicular. The workhorse for walls, frames and furniture.
   */
  orientedBox(
    cx: number, cy: number, cz: number,
    len: number, h: number, thick: number,
    dx: number, dz: number,
    hex: number,
  ): void {
    const c = rgb(hex);
    // Perpendicular in the ground plane.
    const px = -dz;
    const pz = dx;
    const hl = len / 2;
    const hh = h / 2;
    const ht = thick / 2;
    const corner = (u: number, v: number, w: number): [number, number, number] => [
      cx + dx * u + px * w,
      cy + v,
      cz + dz * u + pz * w,
    ];
    const a0 = corner(-hl, -hh, -ht);
    const a1 = corner(hl, -hh, -ht);
    const a2 = corner(hl, hh, -ht);
    const a3 = corner(-hl, hh, -ht);
    const b0 = corner(-hl, -hh, ht);
    const b1 = corner(hl, -hh, ht);
    const b2 = corner(hl, hh, ht);
    const b3 = corner(-hl, hh, ht);
    // -perp / +perp faces
    this.quad(a1, a0, a3, a2, [-px, 0, -pz], c);
    this.quad(b0, b1, b2, b3, [px, 0, pz], c);
    // ends
    this.quad(a0, b0, b3, a3, [-dx, 0, -dz], c);
    this.quad(b1, a1, a2, b2, [dx, 0, dz], c);
    // top / bottom
    this.quad(a3, b3, b2, a2, [0, 1, 0], c);
    this.quad(b0, a0, a1, b1, [0, -1, 0], c);
  }

  /** A square-section bar from point A to point B — railings, slopes, anything tilted. */
  bar(
    ax: number, ay: number, az: number,
    bx: number, by: number, bz: number,
    thick: number, hex: number,
  ): void {
    const c = rgb(hex);
    const dx = bx - ax;
    const dy = by - ay;
    const dz = bz - az;
    const len = Math.hypot(dx, dy, dz) || 1;
    const ux = dx / len;
    const uy = dy / len;
    const uz = dz / len;
    // A stable side vector: cross with world-up unless nearly vertical.
    let sx = -uz;
    let sy = 0;
    let sz = ux;
    const slen = Math.hypot(sx, sz);
    if (slen < 1e-4) {
      sx = 1; sy = 0; sz = 0;
    } else {
      sx /= slen; sz /= slen;
    }
    // Third axis = u × s.
    const tx = uy * sz - uz * sy;
    const ty = uz * sx - ux * sz;
    const tz = ux * sy - uy * sx;
    const ht = thick / 2;
    const p = (ox: number, oy: number, oz: number, du: number, ds: number, dt: number):
      [number, number, number] => [
      ox + ux * du + sx * ds + tx * dt,
      oy + uy * du + sy * ds + ty * dt,
      oz + uz * du + sz * ds + tz * dt,
    ];
    const q0 = p(ax, ay, az, 0, -ht, -ht);
    const q1 = p(bx, by, bz, 0, -ht, -ht);
    const q2 = p(bx, by, bz, 0, ht, -ht);
    const q3 = p(ax, ay, az, 0, ht, -ht);
    const r0 = p(ax, ay, az, 0, -ht, ht);
    const r1 = p(bx, by, bz, 0, -ht, ht);
    const r2 = p(bx, by, bz, 0, ht, ht);
    const r3 = p(ax, ay, az, 0, ht, ht);
    this.quad(q1, q0, q3, q2, [-tx, -ty, -tz], c);
    this.quad(r0, r1, r2, r3, [tx, ty, tz], c);
    this.quad(q0, r0, r3, q3, [-sx, -sy, -sz], c);
    this.quad(r1, q1, q2, r2, [sx, sy, sz], c);
    this.quad(q3, r3, r2, q2, [ux, uy, uz], c);
    this.quad(r0, q0, q1, r1, [-ux, -uy, -uz], c);
  }

  toData(): GeometryData {
    return {
      positions: new Float32Array(this.pos),
      normals: new Float32Array(this.nrm),
      colors: new Float32Array(this.col),
      uvs: this.uv ? new Float32Array(this.uv) : null,
    };
  }
}

// ── the plan ────────────────────────────────────────────────────────────────────────────

export interface Edge {
  readonly ax: number;
  readonly az: number;
  readonly bx: number;
  readonly bz: number;
  readonly len: number;
  readonly dx: number;
  readonly dz: number;
  /** Outward-facing ground normal. */
  readonly nx: number;
  readonly nz: number;
}

export interface Opening {
  readonly edge: number;
  /** Metres along the edge from its `a` end. */
  readonly t0: number;
  readonly t1: number;
  readonly y0: number;
  readonly y1: number;
  readonly kind: 'door' | 'shopfront' | 'window';
  readonly floor: number;
}

export interface StairPlan {
  /** Foot of the flight, on the ground direction it climbs along. */
  readonly x: number;
  readonly z: number;
  readonly dx: number;
  readonly dz: number;
  readonly len: number;
  readonly width: number;
}

export interface SlotAnchor {
  readonly x: number;
  readonly z: number;
  /** Which way the fixture faces — into the room. */
  readonly fx: number;
  readonly fz: number;
}

export interface InteriorPlan {
  readonly poly: Poly;
  readonly bounds: Bounds;
  readonly cx: number;
  readonly cz: number;
  readonly edges: readonly Edge[];
  readonly frontage: number;
  readonly openings: readonly Opening[];
  readonly door: Opening | null;
  readonly stair: StairPlan | null;
  readonly anchors: readonly SlotAnchor[];
  readonly pendants: ReadonlyArray<readonly [number, number]>;
  readonly columns: ReadonlyArray<readonly [number, number]>;
  readonly spawn: { readonly x: number; readonly z: number; readonly yaw: number };
  /**
   * Where quick travel puts the player: the most open standable point OFF the stairwell. On a
   * shed, the fallback flight runs through the middle of the room — arrive there and the ramp
   * physics immediately (and correctly) walks you back down a floor.
   */
  readonly landing: readonly [number, number];
  readonly floors: number;
}

function edgesOf(poly: Poly, cx: number, cz: number): Edge[] {
  const out: Edge[] = [];
  for (let i = 0; i < poly.length; i += 1) {
    const a = poly[i];
    const b = poly[(i + 1) % poly.length];
    if (!a || !b) continue;
    const dx0 = b[0] - a[0];
    const dz0 = b[1] - a[1];
    const len = Math.hypot(dx0, dz0);
    if (len < 0.3) continue;
    const dx = dx0 / len;
    const dz = dz0 / len;
    let nx = -dz;
    let nz = dx;
    const mx = (a[0] + b[0]) / 2;
    const mz = (a[1] + b[1]) / 2;
    if (nx * (cx - mx) + nz * (cz - mz) > 0) {
      nx = -nx;
      nz = -nz;
    }
    out.push({ ax: a[0], az: a[1], bx: b[0], bz: b[1], len, dx, dz, nx, nz });
  }
  return out;
}

/** The wall that faces the street: outward normal closest to the frontage bearing, length-weighted. */
export function pickFrontage(edges: readonly Edge[], frontageDeg: number | null): number {
  if (edges.length === 0) return 0;
  let best = 0;
  let bestScore = -Infinity;
  if (frontageDeg === null) {
    for (let i = 0; i < edges.length; i += 1) {
      const e = edges[i];
      if (e && e.len > bestScore) {
        bestScore = e.len;
        best = i;
      }
    }
    return best;
  }
  const rad = (frontageDeg * Math.PI) / 180;
  // Bearing is clockwise from north; local north is −Z, east is +X.
  const tx = Math.sin(rad);
  const tz = -Math.cos(rad);
  for (let i = 0; i < edges.length; i += 1) {
    const e = edges[i];
    if (!e) continue;
    const score = (e.nx * tx + e.nz * tz) * Math.sqrt(Math.max(e.len, 0.01));
    if (score > bestScore) {
      bestScore = score;
      best = i;
    }
  }
  return best;
}

/** Lay windows (and on the frontage ground floor, the door) along one edge of one floor. */
function layOpenings(
  e: Edge, edgeIdx: number, floor: number, isFrontage: boolean,
): Opening[] {
  const out: Opening[] = [];
  const margin = 0.75;
  const usable = e.len - margin * 2;
  if (usable < 1.1) return out;

  const ground = floor === 0;
  const shopfront = isFrontage && ground;
  const w = shopfront ? 1.9 : ground ? 1.4 : 1.3;
  const gap = 0.6;
  const y0 = shopfront ? 0.55 : ground ? 0.85 : 0.95;
  const y1 = shopfront ? 2.6 : ground ? 2.35 : 2.4;
  const kind: Opening['kind'] = shopfront ? 'shopfront' : 'window';

  if (isFrontage && ground) {
    // Door in the middle, windows flanking it.
    const doorW = 1.15;
    const doorT0 = e.len / 2 - doorW / 2;
    out.push({ edge: edgeIdx, t0: doorT0, t1: doorT0 + doorW, y0: 0, y1: 2.3, kind: 'door', floor });
    for (const side of [
      [margin, doorT0 - 0.35] as const,
      [doorT0 + doorW + 0.35, e.len - margin] as const,
    ]) {
      const span = side[1] - side[0];
      const n = Math.max(0, Math.floor((span + gap) / (w + gap)));
      if (n === 0) continue;
      const total = n * w + (n - 1) * gap;
      let t = side[0] + (span - total) / 2;
      for (let i = 0; i < n; i += 1) {
        out.push({ edge: edgeIdx, t0: t, t1: t + w, y0, y1, kind, floor });
        t += w + gap;
      }
    }
    return out;
  }

  const n = Math.max(0, Math.floor((usable + gap) / (w + gap)));
  if (n === 0) return out;
  const total = n * w + (n - 1) * gap;
  let t = margin + (usable - total) / 2;
  for (let i = 0; i < n; i += 1) {
    out.push({ edge: edgeIdx, t0: t, t1: t + w, y0, y1, kind, floor });
    t += w + gap;
  }
  return out;
}

/** Points inside the footprint with decent wall clearance, best-first. */
function openPoints(poly: Poly, b: Bounds, radius: number): Array<{ x: number; z: number; clear: number }> {
  const span = Math.max(b.maxX - b.minX, b.maxZ - b.minZ);
  const step = Math.max(0.5, span / 16);
  const pts: Array<{ x: number; z: number; clear: number }> = [];
  for (let x = b.minX + step * 0.5; x <= b.maxX; x += step) {
    for (let z = b.minZ + step * 0.5; z <= b.maxZ; z += step) {
      if (!pointInPoly(x, z, poly)) continue;
      const c = clearance(x, z, poly);
      if (c >= radius) pts.push({ x, z, clear: c });
    }
  }
  pts.sort((p, q) => q.clear - p.clear);
  return pts;
}

/**
 * Plan the whole building once per entry: pure geometry in, pure layout out. Fixtures and
 * reachable floors vary per rebuild; the plan does not.
 */
export function planInterior(
  poly: Poly,
  frontageDeg: number | null,
  floors: number,
): InteriorPlan {
  const b = boundsOf(poly);
  // Interior centre: the most open point, not the vertex average — an L-shaped footprint's
  // average lands outside the walls.
  const open = openPoints(poly, b, 0.4);
  const first = open[0];
  const cx = first ? first.x : (b.minX + b.maxX) / 2;
  const cz = first ? first.z : (b.minZ + b.maxZ) / 2;

  const edges = edgesOf(poly, cx, cz);
  const frontage = pickFrontage(edges, frontageDeg);

  const openings: Opening[] = [];
  let door: Opening | null = null;
  for (let f = 0; f < floors; f += 1) {
    for (let i = 0; i < edges.length; i += 1) {
      const e = edges[i];
      if (!e || e.len < 2.6) continue;
      for (const o of layOpenings(e, i, f, i === frontage)) {
        if (o.kind === 'door') door = o;
        openings.push(o);
      }
    }
  }

  /*
   * The stairwell: along the longest non-frontage wall with room for the flight, near its `a`
   * end, pulled off the wall by a step. One position serves every floor — stacked flights are
   * how real buildings do it, and it means one hole shape for every slab.
   */
  let stair: StairPlan | null = null;
  if (floors > 1) {
    let bestIdx = -1;
    let bestLen = 0;
    for (let i = 0; i < edges.length; i += 1) {
      const e = edges[i];
      if (!e || i === frontage) continue;
      if (e.len >= STAIR_LEN + 1.6 && e.len > bestLen) {
        bestLen = e.len;
        bestIdx = i;
      }
    }
    // A building whose only long wall IS the frontage still needs stairs somewhere.
    if (bestIdx < 0) {
      for (let i = 0; i < edges.length; i += 1) {
        const e = edges[i];
        if (e && e.len >= STAIR_LEN + 1.6 && e.len > bestLen) {
          bestLen = e.len;
          bestIdx = i;
        }
      }
    }
    const e = bestIdx >= 0 ? edges[bestIdx] : undefined;
    if (e) {
      const inset = WALL_T + STAIR_W / 2 + 0.12;
      const t0 = 0.8;
      stair = {
        x: e.ax + e.dx * t0 + e.nx * -inset,
        z: e.az + e.dz * t0 + e.nz * -inset,
        dx: e.dx,
        dz: e.dz,
        len: STAIR_LEN,
        width: STAIR_W,
      };
    } else {
      // Tiny footprint: a ladder-steep flight through the middle beats no stairs at all.
      stair = { x: cx - 1.2, z: cz, dx: 1, dz: 0, len: Math.min(2.6, (b.maxX - b.minX) * 0.7), width: 0.9 };
    }
  }

  // Fixture anchors along the walls, skipping the door's swing and the stairwell.
  const anchors: SlotAnchor[] = [];
  const spacing = 1.7;
  for (let i = 0; i < edges.length && anchors.length < 30; i += 1) {
    const e = edges[i];
    if (!e || e.len < 1.8) continue;
    for (let t = 1.0; t <= e.len - 1.0 && anchors.length < 30; t += spacing) {
      if (door && i === door.edge && t > door.t0 - 1.1 && t < door.t1 + 1.1) continue;
      const inset = WALL_T + 0.38;
      const x = e.ax + e.dx * t - e.nx * inset;
      const z = e.az + e.dz * t - e.nz * inset;
      if (stair) {
        const relX = x - stair.x;
        const relZ = z - stair.z;
        const along = relX * stair.dx + relZ * stair.dz;
        const across = Math.abs(relX * -stair.dz + relZ * stair.dx);
        if (along > -1 && along < stair.len + 1 && across < stair.width / 2 + 0.9) continue;
      }
      if (!pointInPoly(x, z, poly)) continue;
      anchors.push({ x, z, fx: -e.nx, fz: -e.nz });
    }
  }

  // Pendant lights: the open centre, plus satellites when the room is big.
  const pendants: Array<readonly [number, number]> = [];
  for (const p of open) {
    if (pendants.every(([px, pz]) => Math.hypot(px - p.x, pz - p.z) > 3.4)) {
      pendants.push([p.x, p.z]);
      if (pendants.length >= 4) break;
    }
  }
  if (pendants.length === 0) pendants.push([cx, cz]);

  // Structural columns on long spans, kept out of the walkway ring.
  const columns: Array<readonly [number, number]> = [];
  const span = Math.max(b.maxX - b.minX, b.maxZ - b.minZ);
  if (span > 10) {
    for (const p of open) {
      if (p.clear < 2.2 || p.clear > span / 2 - 1) continue;
      if (Math.hypot(p.x - cx, p.z - cz) < 2.4) continue;
      if (stair) {
        const relX = p.x - stair.x;
        const relZ = p.z - stair.z;
        const along = relX * stair.dx + relZ * stair.dz;
        const across = Math.abs(relX * -stair.dz + relZ * stair.dx);
        if (along > -1 && along < stair.len + 1 && across < stair.width / 2 + 1) continue;
      }
      if (columns.every(([qx, qz]) => Math.hypot(qx - p.x, qz - p.z) > 4.5)) {
        columns.push([p.x, p.z]);
        if (columns.length >= 4) break;
      }
    }
  }

  // Spawn just inside the door, facing the room; without a door, the open centre.
  let spawn = { x: cx, z: cz, yaw: 0 };
  if (door) {
    const e = edges[door.edge];
    if (e) {
      const tMid = (door.t0 + door.t1) / 2;
      const sx = e.ax + e.dx * tMid - e.nx * 1.35;
      const sz = e.az + e.dz * tMid - e.nz * 1.35;
      const dx0 = cx - sx;
      const dz0 = cz - sz;
      spawn = { x: sx, z: sz, yaw: dx0 === 0 && dz0 === 0 ? 0 : Math.atan2(-dx0, -dz0) };
    }
  }

  // The quick-travel landing: best open point clear of the flight (see the field's doc).
  const offStairs = (x: number, z: number): boolean => {
    if (!stair) return true;
    const relX = x - stair.x;
    const relZ = z - stair.z;
    const along = relX * stair.dx + relZ * stair.dz;
    const across = Math.abs(relX * -stair.dz + relZ * stair.dx);
    return !(along > -0.8 && along < stair.len + 0.8 && across < stair.width / 2 + 0.55);
  };
  let landing: readonly [number, number] = [cx, cz];
  if (!offStairs(cx, cz)) {
    const clear = open.find((p) => offStairs(p.x, p.z));
    if (clear) landing = [clear.x, clear.z];
    else if (spawn.x !== cx || spawn.z !== cz) landing = [spawn.x, spawn.z];
  }

  return {
    poly, bounds: b, cx, cz, edges, frontage, openings, door, stair, anchors,
    pendants, columns, spawn, landing, floors,
  };
}

// ── geometry from the plan ──────────────────────────────────────────────────────────────

export interface InteriorBuild {
  readonly opaque: GeometryData;
  readonly floor: GeometryData;
  readonly glass: GeometryData;
  readonly glow: GeometryData;
  /** World-space boxes for aiming and collision: [minX,minY,minZ,maxX,maxY,maxZ, …] per fixture. */
  readonly fixtureBoxes: ReadonlyArray<{ id: string; kind: string; box: readonly number[] }>;
  /** Pendant positions with their floor, for real lights. */
  readonly lights: ReadonlyArray<{ x: number; y: number; z: number; floor: number }>;
}

/** Openings on one edge of one floor, sorted; wall pieces fill the gaps. */
function wallPieces(
  e: Edge, edgeIdx: number, floor: number, openings: readonly Opening[],
): Array<{ t0: number; t1: number; y0: number; y1: number }> {
  const H = FLOOR_H;
  const mine = openings
    .filter((o) => o.edge === edgeIdx && o.floor === floor)
    .sort((a, b) => a.t0 - b.t0);
  const pieces: Array<{ t0: number; t1: number; y0: number; y1: number }> = [];
  let cursor = 0;
  for (const o of mine) {
    if (o.t0 > cursor + 1e-3) pieces.push({ t0: cursor, t1: o.t0, y0: 0, y1: H });
    if (o.y0 > 1e-3) pieces.push({ t0: o.t0, t1: o.t1, y0: 0, y1: o.y0 });
    if (o.y1 < H - 1e-3) pieces.push({ t0: o.t0, t1: o.t1, y0: o.y1, y1: H });
    cursor = o.t1;
  }
  if (cursor < e.len - 1e-3) pieces.push({ t0: cursor, t1: e.len, y0: 0, y1: H });
  return pieces;
}

export function buildInterior(
  plan: InteriorPlan,
  fixtures: ReadonlyArray<InteriorFixture>,
): InteriorBuild {
  const mb = new MeshBuilder();
  const fb = new MeshBuilder(true);
  const gb = new MeshBuilder();
  const lb = new MeshBuilder();
  const H = FLOOR_H;
  const { poly, edges, openings, stair } = plan;

  const stairsAt = new Set(fixtures.filter((f) => f.kind === 'stairs').map((f) => f.floor));

  // ── slabs ─────────────────────────────────────────────────────────────────────────────
  const flat: number[] = [];
  for (const p of poly) flat.push(p[0], p[1]);
  const plainTri = earcut(flat, undefined, 2);

  // The stairwell hole, as a rectangle ring in the slab above any owned staircase.
  let holeTri: number[] | null = null;
  let holeFlat: number[] | null = null;
  if (stair) {
    const px = -stair.dz;
    const pz = stair.dx;
    const hw = stair.width / 2;
    const x0 = stair.x;
    const z0 = stair.z;
    const ring = [
      [x0 + px * hw, z0 + pz * hw],
      [x0 + stair.dx * stair.len + px * hw, z0 + stair.dz * stair.len + pz * hw],
      [x0 + stair.dx * stair.len - px * hw, z0 + stair.dz * stair.len - pz * hw],
      [x0 - px * hw, z0 - pz * hw],
    ];
    holeFlat = [...flat];
    const holeStart = holeFlat.length / 2;
    for (const [hx, hz] of ring) holeFlat.push(hx ?? 0, hz ?? 0);
    holeTri = earcut(holeFlat, [holeStart], 2);
  }

  const floorCol: [number, number, number] = [1, 1, 1]; // tinted by the plank texture
  const upperCol: [number, number, number] = [0.88, 0.86, 0.82];
  const ceilCol = rgb(C.ceiling);

  /*
   * Slab faces. earcut normalises winding internally, so its output orientation is FIXED no
   * matter which way the input ring winds — and measured against the real geometry (the
   * `floorTriY` debug seam) that fixed orientation faces DOWN in our x/z frame. Reversing the
   * input ring therefore fixes nothing; the top faces must have their vertex order flipped at
   * emit, exactly as the Phase 1 geometry worker does for roofs. Armchair-deriving this sign
   * got it wrong twice; trust the seam.
   */
  const emitSlab = (
    tri: number[], verts: number[], y: number,
    topCol: readonly [number, number, number] | null,
    bottomCol: readonly [number, number, number] | null,
    intoFloorMesh: boolean,
  ): void => {
    const target = intoFloorMesh ? fb : mb;
    for (let k = 0; k < tri.length; k += 3) {
      const i0 = tri[k];
      const i1 = tri[k + 1];
      const i2 = tri[k + 2];
      if (i0 === undefined || i1 === undefined || i2 === undefined) continue;
      const ax = verts[i0 * 2] ?? 0;
      const az = verts[i0 * 2 + 1] ?? 0;
      const bx = verts[i1 * 2] ?? 0;
      const bz = verts[i1 * 2 + 1] ?? 0;
      const cxx = verts[i2 * 2] ?? 0;
      const czz = verts[i2 * 2 + 1] ?? 0;
      if (topCol) target.tri(ax, y, az, cxx, y, czz, bx, y, bz, 0, 1, 0, topCol);
      if (bottomCol) mb.tri(ax, y - 0.12, az, bx, y - 0.12, bz, cxx, y - 0.12, czz, 0, -1, 0, bottomCol);
    }
  };

  for (let f = 0; f < plan.floors; f += 1) {
    const y = f * H;
    const useHole = f > 0 && stairsAt.has(f - 1) && holeTri && holeFlat;
    const tri = useHole ? holeTri! : plainTri;
    const verts = useHole ? holeFlat! : flat;
    // Floor of f (textured planks on the ground floor, painted boards above), and the same
    // triangulation flipped as the ceiling of the storey below.
    emitSlab(tri, verts, y, f === 0 ? floorCol : upperCol, f > 0 ? ceilCol : null, true);
  }
  // Roof underside closes the top storey.
  emitSlab(plainTri, flat, plan.floors * H, null, ceilCol, false);

  // ── walls, per edge per floor ─────────────────────────────────────────────────────────
  for (let f = 0; f < plan.floors; f += 1) {
    const yBase = f * H;
    for (let i = 0; i < edges.length; i += 1) {
      const e = edges[i];
      if (!e) continue;
      for (const piece of wallPieces(e, i, f, openings)) {
        const mid = (piece.t0 + piece.t1) / 2;
        const cx = e.ax + e.dx * mid - e.nx * (WALL_T / 2);
        const cz = e.az + e.dz * mid - e.nz * (WALL_T / 2);
        const h = piece.y1 - piece.y0;
        mb.orientedBox(
          cx, yBase + piece.y0 + h / 2, cz,
          piece.t1 - piece.t0, h, WALL_T, e.dx, e.dz, C.wallPaint,
        );
      }

      // Skirting and cornice along the whole edge (thin, proud of the wall face).
      const inx = -e.nx;
      const inz = -e.nz;
      const smid = e.len / 2;
      const sx = e.ax + e.dx * smid + inx * (WALL_T + 0.012);
      const sz = e.az + e.dz * smid + inz * (WALL_T + 0.012);
      mb.orientedBox(sx, yBase + 0.055, sz, e.len, 0.11, 0.024, e.dx, e.dz, C.skirting);
      mb.orientedBox(sx, yBase + H - 0.07, sz, e.len, 0.14, 0.02, e.dx, e.dz, C.cornice);
    }
  }

  // ── openings: frames, sills, glass, the door ──────────────────────────────────────────
  const glassCol = rgb(C.glassTint);
  for (const o of openings) {
    const e = edges[o.edge];
    if (!e) continue;
    const yBase = o.floor * H;
    const mid = (o.t0 + o.t1) / 2;
    const w = o.t1 - o.t0;
    const h = o.y1 - o.y0;
    const cx = e.ax + e.dx * mid - e.nx * (WALL_T / 2);
    const cz = e.az + e.dz * mid - e.nz * (WALL_T / 2);
    const yMid = yBase + o.y0 + h / 2;
    const frameT = 0.07;
    const frameD = WALL_T + 0.06;

    if (o.kind === 'door') {
      // Jambs and head.
      mb.orientedBox(cx - e.dx * (w / 2 - frameT / 2), yMid, cz - e.dz * (w / 2 - frameT / 2),
        frameT, h, frameD, e.dx, e.dz, C.frame);
      mb.orientedBox(cx + e.dx * (w / 2 - frameT / 2), yMid, cz + e.dz * (w / 2 - frameT / 2),
        frameT, h, frameD, e.dx, e.dz, C.frame);
      mb.orientedBox(cx, yBase + o.y1 - frameT / 2, cz, w, frameT, frameD, e.dx, e.dz, C.frame);
      // The leaf, ajar into the room on the hinge side, with a knob.
      const hingeX = e.ax + e.dx * o.t0 - e.nx * (WALL_T / 2);
      const hingeZ = e.az + e.dz * o.t0 - e.nz * (WALL_T / 2);
      const ang = 0.42; // ~24° open
      const ldx = e.dx * Math.cos(ang) - e.nx * Math.sin(ang);
      const ldz = e.dz * Math.cos(ang) - e.nz * Math.sin(ang);
      const leafW = w - frameT * 2;
      const lx = hingeX + ldx * (frameT + leafW / 2);
      const lz = hingeZ + ldz * (frameT + leafW / 2);
      mb.orientedBox(lx, yBase + (o.y1 - frameT) / 2, lz, leafW, o.y1 - frameT, 0.05, ldx, ldz, C.doorLeaf);
      const kx = hingeX + ldx * (leafW - 0.12);
      const kz = hingeZ + ldz * (leafW - 0.12);
      mb.box(kx, yBase + 1.05, kz, 0.06, 0.06, 0.14, C.doorHandle);
      continue;
    }

    // Window frame: jambs, head, and a sill that sticks out into the room.
    mb.orientedBox(cx - e.dx * (w / 2 - frameT / 2), yMid, cz - e.dz * (w / 2 - frameT / 2),
      frameT, h, frameD, e.dx, e.dz, C.frame);
    mb.orientedBox(cx + e.dx * (w / 2 - frameT / 2), yMid, cz + e.dz * (w / 2 - frameT / 2),
      frameT, h, frameD, e.dx, e.dz, C.frame);
    mb.orientedBox(cx, yBase + o.y1 - frameT / 2, cz, w, frameT, frameD, e.dx, e.dz, C.frame);
    mb.orientedBox(cx, yBase + o.y0 + frameT / 2, cz, w, frameT, frameD, e.dx, e.dz, C.frame);
    mb.orientedBox(cx - e.nx * (WALL_T / 2 + 0.045), yBase + o.y0 - 0.02, cz - e.nz * (WALL_T / 2 + 0.045),
      w + 0.12, 0.05, 0.16, e.dx, e.dz, C.sill);
    // Mullions: one vertical bar; shopfronts get a horizontal transom too.
    mb.orientedBox(cx, yMid, cz, 0.045, h - frameT * 2, 0.045, e.dx, e.dz, C.frame);
    if (o.kind === 'shopfront') {
      mb.orientedBox(cx, yBase + o.y1 - 0.55, cz, w - frameT * 2, 0.045, 0.045, e.dx, e.dz, C.frame);
    }

    // The glass pane, one quad at the wall's mid-plane, drawn double-sided by the material.
    const gx0 = e.ax + e.dx * o.t0 - e.nx * (WALL_T / 2);
    const gz0 = e.az + e.dz * o.t0 - e.nz * (WALL_T / 2);
    const gx1 = e.ax + e.dx * o.t1 - e.nx * (WALL_T / 2);
    const gz1 = e.az + e.dz * o.t1 - e.nz * (WALL_T / 2);
    gb.quad(
      [gx0, yBase + o.y0, gz0], [gx1, yBase + o.y0, gz1],
      [gx1, yBase + o.y1, gz1], [gx0, yBase + o.y1, gz0],
      [-e.nx, 0, -e.nz], glassCol,
    );
  }

  // ── columns, pendants ─────────────────────────────────────────────────────────────────
  for (const [colX, colZ] of plan.columns) {
    for (let f = 0; f < plan.floors; f += 1) {
      const y = f * H;
      mb.box(colX, y + H / 2, colZ, 0.3, H, 0.3, C.column);
      mb.box(colX, y + 0.09, colZ, 0.42, 0.18, 0.42, C.cornice);
      mb.box(colX, y + H - 0.08, colZ, 0.4, 0.16, 0.4, C.cornice);
    }
  }

  const lights: Array<{ x: number; y: number; z: number; floor: number }> = [];
  for (let f = 0; f < plan.floors; f += 1) {
    const y = (f + 1) * H;
    for (const [px, pz] of plan.pendants) {
      mb.bar(px, y - 0.13, pz, px, y - 0.62, pz, 0.02, C.pendantCord);
      // A shallow cone shade from six quads.
      const r0 = 0.06;
      const r1 = 0.26;
      for (let s = 0; s < 6; s += 1) {
        const a0 = (s / 6) * Math.PI * 2;
        const a1 = ((s + 1) / 6) * Math.PI * 2;
        const nx = Math.cos((a0 + a1) / 2);
        const nz = Math.sin((a0 + a1) / 2);
        mb.quad(
          [px + Math.cos(a0) * r0, y - 0.62, pz + Math.sin(a0) * r0],
          [px + Math.cos(a1) * r0, y - 0.62, pz + Math.sin(a1) * r0],
          [px + Math.cos(a1) * r1, y - 0.8, pz + Math.sin(a1) * r1],
          [px + Math.cos(a0) * r1, y - 0.8, pz + Math.sin(a0) * r1],
          [nx, 0.35, nz], rgb(C.pendantShade),
        );
      }
      lb.box(px, y - 0.74, pz, 0.11, 0.11, 0.11, C.bulb);
      lights.push({ x: px, y: y - 0.85, z: pz, floor: f });
    }
  }

  // ── stairs, where they exist ──────────────────────────────────────────────────────────
  if (stair) {
    const px = -stair.dz;
    const pz = stair.dx;
    const steps = 15;
    for (const f of stairsAt) {
      if (f < 0 || f > plan.floors - 2) continue;
      const yBase = f * H;
      for (let s = 0; s < steps; s += 1) {
        const t = (s + 0.5) / steps;
        const sx = stair.x + stair.dx * t * stair.len;
        const sz = stair.z + stair.dz * t * stair.len;
        const rise = (H / steps) * (s + 1);
        mb.orientedBox(sx, yBase + rise - 0.028, sz, stair.len / steps + 0.02, 0.056, stair.width,
          stair.dx, stair.dz, C.stairTread);
        mb.orientedBox(sx - stair.dx * (stair.len / steps) * 0.5, yBase + rise - (H / steps) / 2 - 0.028,
          sz - stair.dz * (stair.len / steps) * 0.5,
          0.03, H / steps, stair.width, stair.dx, stair.dz, C.stairRiser);
      }
      // Handrail on the open side — whichever side of the flight the room's centre is on.
      const roomSide = Math.sign(
        (plan.cx - stair.x) * px + (plan.cz - stair.z) * pz,
      ) || 1;
      const off = (stair.width / 2 + 0.03) * roomSide;
      for (let s = 0; s <= 3; s += 1) {
        const t = s / 3;
        const bx0 = stair.x + stair.dx * t * stair.len + px * off;
        const bz0 = stair.z + stair.dz * t * stair.len + pz * off;
        const by = yBase + H * t;
        mb.bar(bx0, by, bz0, bx0, by + 0.92, bz0, 0.045, C.stairPost);
      }
      mb.bar(
        stair.x + px * off, yBase + 0.95, stair.z + pz * off,
        stair.x + stair.dx * stair.len + px * off, yBase + H + 0.92,
        stair.z + stair.dz * stair.len + pz * off,
        0.055, C.stairRail,
      );
      // A guard rail along the open side of the well upstairs, so the hole reads as meant.
      const upy = yBase + H;
      mb.bar(
        stair.x + px * off, upy + 0.9, stair.z + pz * off,
        stair.x + stair.dx * stair.len + px * off, upy + 0.9,
        stair.z + stair.dz * stair.len + pz * off,
        0.05, C.stairRail,
      );
    }
  }

  // ── fixtures ──────────────────────────────────────────────────────────────────────────
  const fixtureBoxes: Array<{ id: string; kind: string; box: number[] }> = [];
  for (const f of fixtures) {
    if (f.kind === 'stairs') continue;
    const anchor = plan.anchors[f.slot % Math.max(1, plan.anchors.length)] ?? {
      x: plan.cx, z: plan.cz, fx: 0, fz: -1,
    };
    const y = f.floor * H;
    const dx = -anchor.fz; // fixture length axis runs along the wall
    const dz = anchor.fx;

    if (f.kind === 'shelf') {
      const wdt = 1.5;
      const dep = 0.5;
      const hgt = 1.85;
      // Back panel against the wall, uprights, four boards.
      mb.orientedBox(anchor.x - anchor.fx * (dep / 2 - 0.02), y + hgt / 2, anchor.z - anchor.fz * (dep / 2 - 0.02),
        wdt, hgt, 0.04, dx, dz, C.shelfBack);
      for (const side of [-1, 1]) {
        mb.orientedBox(anchor.x + dx * side * (wdt / 2 - 0.025), y + hgt / 2, anchor.z + dz * side * (wdt / 2 - 0.025),
          0.05, hgt, dep, dx, dz, C.shelfWood);
      }
      for (let b2 = 0; b2 < 4; b2 += 1) {
        const by = y + 0.28 + b2 * 0.47;
        mb.orientedBox(anchor.x, by, anchor.z, wdt - 0.1, 0.045, dep - 0.06, dx, dz, C.shelfWood);
        // Goods: a run of little boxes per board, in the item's colour, thinning as stock does.
        if (f.itemId && f.qty > 0) {
          const fullness = Math.max(0.1, Math.min(1, f.qty / 40));
          const count = Math.max(1, Math.round(5 * fullness));
          const gcol = itemColour(f.itemId);
          for (let g = 0; g < count; g += 1) {
            const gt = (g + 0.5) / 5 - 0.5;
            mb.orientedBox(anchor.x + dx * gt * (wdt - 0.3), by + 0.13, anchor.z + dz * gt * (wdt - 0.3),
              0.2, 0.21, 0.24, dx, dz, gcol);
          }
        }
      }
      fixtureBoxes.push({
        id: f.id, kind: f.kind,
        box: aabb(anchor.x, y, anchor.z, dx, dz, wdt, hgt, dep),
      });
    } else if (f.kind === 'checkout') {
      const wdt = 1.8;
      const dep = 0.62;
      // Counter body, lighter top, register with a glowing screen, card reader.
      mb.orientedBox(anchor.x, y + 0.475, anchor.z, wdt, 0.95, dep, dx, dz, C.counter);
      mb.orientedBox(anchor.x, y + 0.975, anchor.z, wdt + 0.08, 0.05, dep + 0.08, dx, dz, C.counterTop);
      mb.orientedBox(anchor.x + dx * 0.45, y + 1.16, anchor.z + dz * 0.45, 0.34, 0.32, 0.3, dx, dz, C.register);
      lb.orientedBox(anchor.x + dx * 0.45 - anchor.fx * 0.12, y + 1.2, anchor.z + dz * 0.45 - anchor.fz * 0.12,
        0.26, 0.18, 0.02, dx, dz, C.registerScreen);
      mb.orientedBox(anchor.x - dx * 0.55, y + 1.05, anchor.z - dz * 0.55, 0.12, 0.14, 0.1, dx, dz, C.register);
      fixtureBoxes.push({
        id: f.id, kind: f.kind,
        box: aabb(anchor.x, y, anchor.z, dx, dz, wdt, 1.35, dep),
      });
    } else if (f.kind === 'storage') {
      const wdt = 2.2;
      const dep = 0.85;
      const hgt = 2.25;
      for (const side of [-1, 1]) {
        for (const face of [-1, 1]) {
          mb.orientedBox(
            anchor.x + dx * side * (wdt / 2 - 0.04) + anchor.fx * face * (dep / 2 - 0.04),
            y + hgt / 2,
            anchor.z + dz * side * (wdt / 2 - 0.04) + anchor.fz * face * (dep / 2 - 0.04),
            0.08, hgt, 0.08, dx, dz, C.rackSteel);
        }
      }
      for (let lvl = 0; lvl < 2; lvl += 1) {
        const by = y + 0.95 + lvl * 0.95;
        mb.orientedBox(anchor.x, by, anchor.z, wdt, 0.09, dep, dx, dz, C.rackBeam);
        mb.orientedBox(anchor.x, by + 0.07, anchor.z, wdt - 0.15, 0.05, dep - 0.1, dx, dz, C.pallet);
      }
      // A few crates on the beams say "warehouse" even before stock exists.
      mb.orientedBox(anchor.x - dx * 0.55, y + 1.19, anchor.z - dz * 0.55, 0.5, 0.42, 0.5, dx, dz, C.pallet);
      mb.orientedBox(anchor.x + dx * 0.4, y + 2.14, anchor.z + dz * 0.4, 0.55, 0.4, 0.5, dx, dz, 0x8a7350);
      fixtureBoxes.push({
        id: f.id, kind: f.kind,
        box: aabb(anchor.x, y, anchor.z, dx, dz, wdt, hgt, dep),
      });
    }
  }

  return {
    opaque: mb.toData(),
    floor: fb.toData(),
    glass: gb.toData(),
    glow: lb.toData(),
    fixtureBoxes,
    lights,
  };
}

/** Conservative world AABB for an oriented footprint — aim and collision do not need exactness. */
function aabb(
  x: number, y: number, z: number,
  dx: number, dz: number, len: number, h: number, dep: number,
): number[] {
  const ex = Math.abs(dx) * len / 2 + Math.abs(-dz) * dep / 2 + 0.05;
  const ez = Math.abs(dz) * len / 2 + Math.abs(dx) * dep / 2 + 0.05;
  return [x - ex, y, z - ez, x + ex, y + h, z + ez];
}
